export interface MentionCandidate {
  id: string;
  name: string;
  /** Shown next to the name in the picker, e.g. "Account" or "Cliente". */
  hint?: string;
}

/** The "@que" being typed right before the caret, if any. */
export function getMentionQuery(text: string, caret: number): { query: string; start: number } | null {
  const before = text.slice(0, caret);
  const match = /(^|\s)@([^\s@]{0,40})$/.exec(before);
  if (!match) return null;
  return { query: match[2], start: before.length - match[2].length - 1 };
}

/** Replaces "@que" with "@Full Name " and returns the new caret position. */
export function insertMention(text: string, start: number, caret: number, name: string): { text: string; caret: number } {
  const inserted = `@${name} `;
  return {
    text: text.slice(0, start) + inserted + text.slice(caret),
    caret: start + inserted.length,
  };
}

function normalize(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function filterCandidates(candidates: MentionCandidate[], query: string, limit = 6): MentionCandidate[] {
  const q = normalize(query);
  return candidates
    .filter(c => normalize(c.name).split(/\s+/).some(part => part.startsWith(q)) || normalize(c.name).startsWith(q))
    .slice(0, limit);
}

const boundary = (ch: string | undefined) => ch === undefined || !/[\p{L}\p{N}_]/u.test(ch);

function findMention(text: string, name: string, from = 0): number {
  const token = `@${name}`;
  let at = text.indexOf(token, from);
  while (at !== -1) {
    if (boundary(text[at + token.length])) return at;
    at = text.indexOf(token, at + 1);
  }
  return -1;
}

/** Ids of the candidates still tagged in the final text (a removed tag doesn't notify). */
export function extractMentionIds(text: string, candidates: MentionCandidate[]): string[] {
  // Longest names first so "@Ana Silva" wins over "@Ana"
  // and each tag is consumed once found (so it can't also match "@Ana")
  const sorted = [...candidates].sort((a, b) => b.name.length - a.name.length);
  const ids: string[] = [];
  let remaining = text;
  for (const c of sorted) {
    if (!c.name) continue;
    let at = findMention(remaining, c.name);
    if (at === -1) continue;
    if (!ids.includes(c.id)) ids.push(c.id);
    while (at !== -1) {
      const len = c.name.length + 1;
      remaining = remaining.slice(0, at) + '\u0000'.repeat(len) + remaining.slice(at + len);
      at = findMention(remaining, c.name, at + len);
    }
  }
  return ids;
}

/** Splits text so tagged names can be highlighted. */
export function splitMentions(text: string, names: string[]): { text: string; mention: boolean }[] {
  const sorted = [...new Set(names.filter(Boolean))].sort((a, b) => b.length - a.length);
  if (sorted.length === 0) return [{ text, mention: false }];
  const parts: { text: string; mention: boolean }[] = [];
  let i = 0;
  while (i < text.length) {
    let hit: { at: number; len: number } | null = null;
    for (const name of sorted) {
      const at = findMention(text, name, i);
      if (at !== -1 && (!hit || at < hit.at)) hit = { at, len: name.length + 1 };
    }
    if (!hit) break;
    if (hit.at > i) parts.push({ text: text.slice(i, hit.at), mention: false });
    parts.push({ text: text.slice(hit.at, hit.at + hit.len), mention: true });
    i = hit.at + hit.len;
  }
  if (i < text.length) parts.push({ text: text.slice(i), mention: false });
  return parts;
}
